import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { PhoneIcon, WhatsappLogoIcon } from '@phosphor-icons/react';

interface AgentTypeSelectionProps {
  selectedType: 'voice' | 'whatsapp' | null;
  onTypeChange: (type: 'voice' | 'whatsapp') => void;
  onValidationChange: (isValid: boolean) => void;
}

const AgentTypeSelection = ({
  selectedType,
  onTypeChange,
  onValidationChange,
}: AgentTypeSelectionProps) => {
  useEffect(() => {
    onValidationChange(selectedType !== null);
  }, [selectedType, onValidationChange]);

  const options = [
    {
      type: 'voice' as const,
      icon: PhoneIcon,
      title: 'Voice Agent',
      description: 'AI agent that handles phone calls and voice interactions',
      color: '#5A4CE6',
    },
    {
      type: 'whatsapp' as const,
      icon: WhatsappLogoIcon,
      title: 'WhatsApp Agent',
      description: 'AI agent that handles WhatsApp messages and conversations',
      color: '#25D366',
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: '900px',
        margin: '0 auto',
        py: 4,
      }}
    >
      {/* Heading */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: 'custom.textBlue',
          fontSize: '18px',
          mb: 1,
          textAlign: 'center',
        }}
      >
        What kind of agent do you want?
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: 'lightGray.text',
          mb: 4,
          textAlign: 'center',
        }}
      >
        Choose the type of AI agent you'd like to create for your business
      </Typography>

      {/* Options */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
        }}
      >
        {options.map(option => {
          const isSelected = selectedType === option.type;
          const Icon = option.icon;

          return (
            <Box
              key={option.type}
              onClick={() => onTypeChange(option.type)}
              sx={{
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: isSelected ? 'primary.main' : 'lightGray.main',
                borderRadius: '12px',
                p: 4,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                  borderColor: isSelected
                    ? 'primary.main'
                    : 'custom.secondaryBlue',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(90, 76, 230, 0.1)',
                },
              }}
            >
              {/* Selected indicator */}
              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ color: 'white', fontSize: '14px' }}>
                    ✓
                  </Typography>
                </Box>
              )}

              {/* Icon */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '12px',
                  bgcolor: `${option.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Icon size={32} color={option.color} weight="duotone" />
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'custom.textBlue',
                  mb: 1,
                }}
              >
                {option.title}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  lineHeight: 1.5,
                }}
              >
                {option.description}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AgentTypeSelection;
